import React, { useState, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { parseCSV } from '../../utils/csvHelpers';
import { useCRM } from '../../context/CRMContext';

const ImportCSVModal = ({ onClose }) => {
  const { bulkAddContacts, companies } = useCRM();
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);

  const contactFields = [
    { key: 'name', label: 'Navn', required: true },
    { key: 'email', label: 'E-post', required: true },
    { key: 'title', label: 'Tittel', required: false },
    { key: 'phone', label: 'Telefon', required: false },
    { key: 'role_in_buying', label: 'Rolle', required: false },
    { key: 'linkedin', label: 'LinkedIn URL', required: false },
  ];

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      try {
        const parsed = await parseCSV(selectedFile);
        if (parsed.length > 0) {
          setData(parsed);
          const firstRowKeys = Object.keys(parsed[0]);
          setHeaders(firstRowKeys);
          const initialMapping = {};
          contactFields.forEach(field => {
            const match = firstRowKeys.find(h => h.toLowerCase().includes(field.key.toLowerCase()) || h.toLowerCase().includes(field.label.toLowerCase()));
            if (match) initialMapping[field.key] = match;
          });
          setMapping(initialMapping);
          setStep(2);
        }
      } catch (err) {
        console.error("Error parsing CSV:", err);
      }
    }
  };

  const handleImport = () => {
    const formattedData = data.map(row => {
      const contact = {};
      contactFields.forEach(field => {
        contact[field.key] = row[mapping[field.key]] || '';
      });
      contact.company_id = companies[0]?.company_id;
      return contact;
    });
    bulkAddContacts(formattedData);
    onClose();
  };

  return (
    <div className="space-y-6">
      {step === 1 && (
        <div onClick={() => fileInputRef.current?.click()} className="border-4 border-dashed border-slate-100 rounded-[2rem] p-12 text-center hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer group">
          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" className="hidden" />
          <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-sm">
            <SafeIcon icon="☁️" className="w-10 h-10 text-slate-400 group-hover:text-primary-600" />
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2">Last opp CSV-fil</h3>
          <p className="text-slate-500 font-medium">Klikk for å velge en fil eller dra den hit</p>
          <div className="mt-6 flex items-center justify-center gap-4">
            <span className="px-3 py-1 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-400 uppercase tracking-widest">Maks 5MB</span>
            <span className="px-3 py-1 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-400 uppercase tracking-widest">UTF-8 Format</span>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
            <SafeIcon icon="⚠️" className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm font-medium text-amber-800">
              Koble kolonnene fra din CSV-fil til feltene i QuestCRM.
            </p>
          </div>
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {contactFields.map(field => (
              <div key={field.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${field.required ? 'bg-rose-500' : 'bg-slate-300'}`} />
                  <span className="font-bold text-slate-700">{field.label}</span>
                  {field.required && <span className="text-[10px] font-black text-rose-500 uppercase">Påkrevd</span>}
                </div>
                <select value={mapping[field.key] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))} className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm font-bold text-slate-700 focus:ring-2 focus:ring-primary-500 outline-none min-w-[200px]">
                  <option value="">Velg kolonne...</option>
                  {headers.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="flex gap-4 pt-4">
            <button onClick={() => setStep(1)} className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all">
              Tilbake
            </button>
            <button onClick={() => setStep(3)} disabled={!contactFields.filter(f => f.required).every(f => mapping[f.key])} className="flex-3 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all disabled:opacity-50">
              Se gjennom ({data.length} rader)
            </button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <SafeIcon icon="✅" className="w-8 h-8 text-emerald-500" />
            </div>
            <h4 className="text-xl font-black text-slate-900 mb-1">Klar for import</h4>
            <p className="text-slate-500 font-medium">Vi fant {data.length} kontakter som er klare til å legges til.</p>
          </div>
          <div className="border border-slate-100 rounded-2xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[10px]">Navn</th>
                  <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[10px]">E-post</th>
                  <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[10px]">Tittel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.slice(0, 5).map((row, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 font-bold text-slate-700">{row[mapping.name]}</td>
                    <td className="px-4 py-3 text-slate-500">{row[mapping.email]}</td>
                    <td className="px-4 py-3 text-slate-500">{row[mapping.title] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 5 && (
              <div className="px-4 py-2 bg-slate-50 text-[10px] font-bold text-slate-400 text-center uppercase tracking-widest">
                + {data.length - 5} flere rader
              </div>
            )}
          </div>
          <div className="flex gap-4">
            <button onClick={() => setStep(2)} className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all">
              Endre kobling
            </button>
            <button onClick={handleImport} className="flex-3 py-4 bg-primary-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-primary-700 transition-all shadow-xl shadow-primary-200">
              Fullfør Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportCSVModal;